import { motion, useReducedMotion } from "framer-motion";
import { mediaUrl } from "@/lib/api";

const TEXTURE =
  "https://images.unsplash.com/photo-1616410731303-6affae095a0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHw0fHxzdWJ0bGUlMjB0ZXh0dXJlZCUyMHBhcGVyJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODcxNjU5NDB8MA&ixlib=rb-4.1.0&q=85&w=1400";

/**
 * Editorial image frame. Shows the real photograph when one has been uploaded,
 * otherwise an elegant labelled placeholder.
 */
export function ImageFrame({
  src,
  label = "BORGO PHOTO",
  alt = "",
  ratio = "4 / 5",
  className = "",
  dark = false,
  priority = false,
  sizes = "(max-width: 768px) 100vw, 50vw",
  testId,
}) {
  const reduce = useReducedMotion();
  const url = mediaUrl(src);
  const local = typeof src === "string" && src.startsWith("/api/files/");
  const srcSet = local
    ? [480, 768, 1200, 1800].map((w) => `${mediaUrl(src)}?w=${w} ${w}w`).join(", ")
    : undefined;
  return (
    <figure
      className={`relative overflow-hidden bg-beige ${className}`}
      style={{ aspectRatio: ratio }}
      data-testid={testId}
    >
      {url ? (
        <motion.img
          src={local ? `${url}?w=1200` : url}
          srcSet={srcSet}
          sizes={sizes}
          alt={alt || label}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : undefined}
          decoding="async"
          className="h-full w-full object-cover"
          initial={reduce ? undefined : { scale: 1.08, opacity: 0 }}
          whileInView={reduce ? undefined : { scale: 1, opacity: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1.4, ease: [0.22, 0.61, 0.36, 1] }}
        />
      ) : (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-4"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--beige) / ${dark ? 0.35 : 0.72}), hsl(var(--beige) / ${
              dark ? 0.55 : 0.82
            })), url(${TEXTURE})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          role="img"
          aria-label={alt || label}
        >
          <svg width="46" height="14" viewBox="0 0 46 14" aria-hidden="true" className="opacity-60">
            <path
              d="M1 7h13M32 7h13M18 7c0-3 2-5 5-5s5 2 5 5-2 5-5 5-5-2-5-5z"
              fill="none"
              stroke="hsl(var(--wood))"
              strokeWidth="0.8"
            />
          </svg>
          <span className="eyebrow text-wood/70 px-6 text-center">{label}</span>
        </div>
      )}
      <span className="pointer-events-none absolute inset-0 border border-wood/15" aria-hidden="true" />
    </figure>
  );
}

export default ImageFrame;
